
import noteAPI from '@/api/notes';
import { getRouteForPath } from '@/helpers/notePathHelper';
import intentManager from '@/helpers/intentManager';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'NoteEntry',
  props: {
    note: {
      type: Object,
      required: true,
    },
    client: {
      type: Object,
      required: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('user', ['currentLogin', 'currentAccount']),
    ...mapGetters('user', ['isClient', 'isEmployee']),

    noteVisibility() {
      return this.note.visible_to_clients ? 'VISIBLE TO CLIENT' : 'PRIVATE';
    },

    noteVisibilityColour() {
      if (this.isClient) return null;

      return this.note.visible_to_clients ? 'text-p-green-dark' : 'text-p-pink';
    },

    noteAuthor() {
      const note = this.note;
      if (!note.account && !note.author_name) {
        return null;
      }
      return {
        name: note.author_name ?? note.account?.name ?? 'Plannr System',
        avatarUrl: note.author_name ? null : note.account?.photo_url ?? null,
      };
    },

    noteBelongsToAccount() {
      if (!this.note.account || !this.currentAccount) {
        return false;
      }
      return this.note.account.uuid === this.currentAccount.uuid;
    },

    /*
      Your own note is always yours to change - the notes:update / notes:delete permissions
      are what let an employee change somebody else's note. Mirrors NotePolicy.
    */
    canEditNote() {
      if (!this.currentLogin.has_write_permission) {
        return false;
      }

      return !this.readOnly && (this.noteBelongsToAccount || (this.isEmployee && this.can('notes:update')));
    },
    canDeleteNote() {
      if (!this.currentLogin.has_delete_permission) {
        return false;
      }

      return !this.readOnly && (this.noteBelongsToAccount || (this.isEmployee && this.can('notes:delete')));
    },
    sanitizedContents() {
      return this.$helpers.sanitizeHtmlAndStyling(this.note.contents, { ADD_ATTR: ['target'] });
    },
    showNotableName() {
      return !!this.note.notable?.name && this.note.notable_type !== 'risk';
    },
    showRiskCompletedAt() {
      return !!this.note.notable?.completed_at && this.note.notable_type === 'risk';
    },
    showCheckProvider() {
      return !!this.note.notable?.provider && this.note.notable_type === 'check';
    },
  },
  methods: {
    removeNote(note) {
      this.$nuxt.$emit('confirm', {
        message: 'Are you sure you would like to remove this note?',
        callback: async () => {
          try {
            await noteAPI.deleteNote(note.uuid);
            this.$emit('reloadNotes');
            this.$nuxt.$emit('notification', 'Note deleted', 'warning');
            this.$nuxt.$emit('refreshNotesTotal');
          } catch (err) {
            if (err.response.status === 403) {
              const message = 'This action is unauthorised.';
              this.$nuxt.$emit('confirm', {
                hideNoButton: true,
                message: message,
              });
            }
          }
        },
      });
    },
    redirect(note) {
      const notableType = note.notable_type;

      switch (notableType) {
        case 'client':
        case 'external':
        case 'employee':
          this.handleClientExternalEmployeeRedirect(note);
          break;
        case 'circle':
          this.handleCircleRedirect(note);
          break;
        case 'plan':
          this.handlePlanRedirect(note);
          break;
        case 'case':
          this.handleCaseRedirect(note);
          break;
        case 'task':
          this.handleTaskRedirect(note);
          break;
        case 'check':
          this.handleCheckRedirect(note);
          break;
        case 'risk':
          this.handleRiskRedirect(note);
          break;
        default:
          console.warn('No redirect handler for notable type:', notableType);
          break;
      }
    },
    handleClientExternalEmployeeRedirect(note) {
      if (!note.notable) {
        console.warn('Cannot redirect: note.notable is null');
        return;
      }
      if (note.path) {
        let route = getRouteForPath(note.path, note.notable_type);
        if (typeof route === 'string') {
          this.$router.push({ name: route, params: { id: note.notable.uuid } });
        } else if (typeof route === 'object') {
          this.$router.push({
            name: route.name,
            params: { id: note.notable.uuid, ...route.params },
          });
        } else {
          this.$router.push('/contacts/' + note.notable.uuid);
        }
      } else {
        this.$router.push('/contacts/' + note.notable.uuid);
      }
    },
    handleCircleRedirect(note) {
      if (!note.notable) {
        console.warn('Cannot redirect: note.notable is null');
        return;
      }
      if (note.path) {
        let routePath = getRouteForPath(note.path, note.notable_type);
        if (typeof routePath === 'string') {
          this.$router.push({ name: routePath, params: { id: note.notable.uuid } });
        } else if (typeof routePath === 'object') {
          this.$router.push({
            name: routePath.name,
            params: { id: note.notable.uuid, ...routePath.params },
          });
        } else {
          this.$router.push('/circle/' + note.notable.uuid);
        }
      } else {
        this.$router.push('/circle/' + note.notable.uuid);
      }
    },
    handlePlanRedirect(note) {
      if (!note.notable) {
        console.warn('Cannot redirect: note.notable is null');
        return;
      }
      this.$router.push('/plans/' + note.notable.uuid);
    },
    handleCaseRedirect(note) {
      if (!note.notable) {
        console.warn('Cannot redirect: note.notable is null');
        return;
      }
      this.$router.push('/cases/' + note.notable.uuid);
    },
    handleTaskRedirect(note) {
      const notableAccountUuid = note.notable?.account?.uuid;

      if (notableAccountUuid) {
        this.$router.push('/contacts/' + notableAccountUuid + '/tasks/' + note.notable.uuid);
      } else if (note?.notable?.taskable) {
        const redirectErrMessage = 'Unable to redirect to task, please navigate manually.';
        const taskable = note.notable.taskable;
        const taskableType = note.notable.taskable_type;

        if (taskableType === 'client') {
          this.$router.push('/contacts/' + taskable.uuid + '/tasks/' + note.notable.uuid);
        } else if (taskableType === 'plan') {
          this.$router.push('/plans/' + taskable.uuid + '/tasks/' + note.notable.uuid);
        } else if (taskableType === 'case') {
          this.$router.push('/cases/' + taskable.uuid + '/tasks/' + note.notable.uuid);
        } else {
          this.$nuxt.$emit('notification', redirectErrMessage, 'warning');
        }
      }
    },
    handleCheckRedirect(note) {
      const notableAccountUuid = note.notable?.account?.uuid;

      if (notableAccountUuid) {
        this.$router.push('/contacts/' + notableAccountUuid + '/checks/' + note.notable.uuid);
      }
    },
    handleRiskRedirect(note) {
      const notableAccountUuid = note.notable?.account?.uuid;

      if (notableAccountUuid) {
        intentManager.redirectWithIntent(
          'viewRiskFromNote',
          {
            risk: note.notable.uuid,
          },
          this.$router,
          '/contacts/' + notableAccountUuid + '/risk'
        );
      }
    },
    startEditNote() {
      this.$emit('editNote', this.note);
    },
  },
};
